/**
 * Reordering nav.yaml as a tree, without touching the git-facing schema.
 *
 * The shapes below are deliberately the LOOSE ones — every field optional,
 * same as `NavItem` in `api.ts` — rather than `@staleless/schema`'s strict
 * union. This module never asks whether an item is legal, only where it sits,
 * so it has no need of the type that enforces the first question; and staying
 * loose means it takes either the sidebar's own rendering data or a freshly
 * `parseNav`'d file without a cast at the call site (the strict shape is a
 * subtype of this one). It is the CALLER's job to hand `serializeNav` a value
 * it still recognises, since moving items around never changes what any one of
 * them is.
 *
 * nav.yaml has no ids — a page is addressed by its content (`page: <id>`), and
 * a group or link only by its position. So every place in the tree is named by
 * a PATH of indices rather than a key, and moving something means resolving
 * that path against the CURRENT tree at the moment of the move, never against
 * one computed earlier and reused.
 *
 * `NavPath` addresses one item: `sections[path[0]].items[path[1]]`, then one
 * more `.items[...]` per remaining index for an item nested inside a group.
 * Every real item has `path.length >= 2` — there is no item that is not inside
 * some section.
 *
 * `NavContainerPath` addresses the array an item could be inserted into:
 * `[sectionIndex]` is that section's own top-level items, and
 * `[sectionIndex, ...groupIndices]` descends through nested groups to reach
 * one of theirs. It is exactly an item's own `NavPath` — descend into THIS
 * item's `.items` rather than stopping at it.
 */
export interface NavItem {
  page?: string
  label?: string
  icon?: string
  href?: string
  items?: NavItem[]
  /**
   * A separator. Carried here so a rule can be dragged like anything else —
   * this module moves items around and never asks what they are, and a
   * separator is exactly the kind of item whose only meaning IS its position.
   */
  separator?: true
}

export interface Nav {
  sections: { label: string; icon?: string; items: NavItem[] }[]
}

export type NavPath = readonly number[]
export type NavContainerPath = readonly number[]

/** Where a drag ends: which array receives the item, and where in it. */
export interface DropTarget {
  containerPath: NavContainerPath
  /**
   * Insert before the item currently at this index, in the container's
   * numbering BEFORE the move. Equal to the container's own length to append —
   * which is also what a section or group header, as a drop target, means.
   */
  beforeIndex: number
}

export type MoveOutcome =
  | { ok: true; nav: Nav }
  /**
   * Refused, and why:
   *  - `cycle` — the target is the dragged group itself, or lives inside it.
   *  - `no-op` — the drop would leave the order exactly as it is.
   *  - `not-found` — `from` or `to` no longer resolves against `nav`, most
   *    likely because nav.yaml changed under the drag.
   */
  /**
   * `not-empty` belongs to sections only: a section that still holds pages
   * cannot be removed without orphaning them.
   */
  | { ok: false; reason: 'cycle' | 'no-op' | 'not-found' | 'not-empty' }

const sameContainerPath = (a: NavContainerPath, b: NavContainerPath): boolean =>
  a.length === b.length && a.every((v, i) => v === b[i])

/**
 * True when dropping into `containerPath` would nest the group at `from`
 * inside itself — directly (the container IS `from`) or through one of its
 * own descendants (the container's path starts with `from`).
 *
 * This is the whole cycle check. It works without knowing whether `from` is
 * even a group: a leaf's path can never be a real container, so it can never be
 * a prefix of one either, and the function simply never fires for a leaf.
 */
export function wouldCycle(from: NavPath, containerPath: NavContainerPath): boolean {
  return containerPath.length >= from.length && from.every((v, i) => containerPath[i] === v)
}

/**
 * A group, narrowed from the loose shape by the one field that actually means
 * it: `page` and `href` are both absent from a group, but neither is required
 * to be, so checking for `items` is the only test that is really asking the
 * right question.
 */
function isGroup(item: NavItem): item is NavItem & { items: NavItem[] } {
  return Array.isArray(item.items)
}

function containerItems(nav: Nav, containerPath: NavContainerPath): NavItem[] | null {
  const [sectionIndex, ...rest] = containerPath
  const section = sectionIndex === undefined ? undefined : nav.sections[sectionIndex]
  if (!section) return null
  let items: NavItem[] = section.items
  for (const index of rest) {
    const node: NavItem | undefined = items[index]
    if (!node) return null
    // A PAGE is a container too — GitBook-style cascading. One that has no
    // children yet resolves as an empty array; `moveNavItem` materialises the
    // real array the moment something is dropped in.
    if (isGroup(node)) items = node.items
    else if (node.page) items = node.items ?? []
    else return null
  }
  return items
}

/** The item at `path`, or `null` if the path no longer resolves. */
export function itemAt(nav: Nav, path: NavPath): NavItem | null {
  if (path.length < 2) return null
  const containerPath = path.slice(0, -1)
  const index = path[path.length - 1]!
  const items = containerItems(nav, containerPath)
  return items?.[index] ?? null
}

/**
 * Move the item at `from` so it lands at `to`, preserving whatever it carries.
 *
 * A group's `items` travel with it because the group is moved as one value,
 * never rebuilt from its children, so there is no step where "move the
 * header" and "move the children" could disagree.
 *
 * Built as ONE pass over `nav` exactly as it stands, rather than a remove
 * pass followed by an insert pass. Two passes need the second one to
 * re-locate `to` in a tree the first pass already changed — and when the
 * removal shifted the very container `to` names, recovering that is either an
 * offset computed by hand (easy to get wrong one level down, inside a group
 * that itself shifted) or a second lookup by identity (which breaks the
 * moment the removal rebuilt an ancestor of the target on its way past it, as
 * `{ ...item, items: … }` naturally does for every group between the root and
 * whichever one changed). One pass sidesteps the whole question: every
 * comparison below reads `containerPath` and `index` from `nav` as it always
 * was, so nothing is ever adjusted for a change that has not happened yet.
 */
export function moveNavItem(nav: Nav, from: NavPath, to: DropTarget): MoveOutcome {
  if (from.length < 2) return { ok: false, reason: 'not-found' }
  const fromContainer = from.slice(0, -1)
  const fromIndex = from[from.length - 1]!

  if (wouldCycle(from, to.containerPath)) return { ok: false, reason: 'cycle' }

  const moving = itemAt(nav, from)
  if (!moving) return { ok: false, reason: 'not-found' }

  const targetItems = containerItems(nav, to.containerPath)
  if (!targetItems || to.beforeIndex < 0 || to.beforeIndex > targetItems.length) {
    return { ok: false, reason: 'not-found' }
  }

  // A drop next to where the item already sits changes nothing. Caught before
  // it moves anything, rather than left to produce a no-op diff.
  if (
    sameContainerPath(to.containerPath, fromContainer) &&
    (to.beforeIndex === fromIndex || to.beforeIndex === fromIndex + 1)
  ) {
    return { ok: false, reason: 'no-op' }
  }

  /**
   * Rebuilds one items array, at its own `containerPath` in the ORIGINAL
   * tree. `index` below is therefore always the original numbering — the
   * source's removal and the target's insertion are two independent checks
   * against the same untouched array, decided in the same walk that copies
   * it, never against each other's output.
   */
  const rebuild = (items: NavItem[], containerPath: NavContainerPath): NavItem[] => {
    const isSource = sameContainerPath(containerPath, fromContainer)
    const isTarget = sameContainerPath(containerPath, to.containerPath)

    const out: NavItem[] = []
    items.forEach((item, index) => {
      if (isTarget && index === to.beforeIndex) out.push(moving)
      if (isSource && index === fromIndex) return // carried to its new spot, not here
      const ownPath = [...containerPath, index]
      // The target path may pass through a page that has no children yet;
      // materialise its items array along that spine so the insert lands.
      const onTargetSpine =
        ownPath.length <= to.containerPath.length &&
        ownPath.every((v, at) => to.containerPath[at] === v)
      out.push(
        isGroup(item)
          ? { ...item, items: rebuild(item.items, ownPath) }
          : onTargetSpine && item.page
            ? { ...item, items: rebuild(item.items ?? [], ownPath) }
            : item,
      )
    })
    if (isTarget && to.beforeIndex === items.length) out.push(moving)
    return out
  }

  const sections = nav.sections.map((section, i) => ({
    ...section,
    items: rebuild(section.items, [i]),
  }))
  return { ok: true, nav: { ...nav, sections } }
}

/** Whether `path` has a sibling before it in its own container. */
export function canMoveUp(nav: Nav, path: NavPath): boolean {
  return path.length >= 2 && path[path.length - 1]! > 0
}

/** Whether `path` has a sibling after it in its own container. */
export function canMoveDown(nav: Nav, path: NavPath): boolean {
  if (path.length < 2) return false
  const container = containerItems(nav, path.slice(0, -1))
  return container !== null && path[path.length - 1]! < container.length - 1
}

/** Swap `path` with the sibling before it, in the same container. */
export function moveUp(nav: Nav, path: NavPath): MoveOutcome {
  if (!canMoveUp(nav, path)) return { ok: false, reason: 'no-op' }
  const containerPath = path.slice(0, -1)
  const index = path[path.length - 1]!
  return moveNavItem(nav, path, { containerPath, beforeIndex: index - 1 })
}

/** Swap `path` with the sibling after it, in the same container. */
export function moveDown(nav: Nav, path: NavPath): MoveOutcome {
  if (!canMoveDown(nav, path)) return { ok: false, reason: 'no-op' }
  const containerPath = path.slice(0, -1)
  const index = path[path.length - 1]!
  return moveNavItem(nav, path, { containerPath, beforeIndex: index + 2 })
}

/**
 * Put a NEW item into the tree, at a place somebody chose.
 *
 * The other half of `moveNavItem`, and it exists for the same reason: a page
 * that is written but not listed is an orphan, and the commit is refused
 * until somebody places it. Until now the product could only MOVE what the
 * navigation already had, so every new page arrived unplaced and the writer
 * was told off for it afterwards. "Place under" on the new-page dialog is
 * this function.
 *
 * `beforeIndex` is the container's own numbering, and equal to its length to
 * append — the same contract `DropTarget` carries, so a caller that has a
 * drop target has an insert.
 */
export function insertNavItem(nav: Nav, item: NavItem, to: DropTarget): MoveOutcome {
  const targetItems = containerItems(nav, to.containerPath)
  if (!targetItems || to.beforeIndex < 0 || to.beforeIndex > targetItems.length) {
    return { ok: false, reason: 'not-found' }
  }

  /**
   * Rebuilt the way a move is, and for the same reason: the target may sit
   * under a page that has no `items` array yet, and the spine has to be
   * materialised on the way down rather than patched afterwards.
   */
  const rebuild = (items: NavItem[], containerPath: NavContainerPath): NavItem[] => {
    const isTarget = sameContainerPath(containerPath, to.containerPath)
    const out: NavItem[] = []
    items.forEach((child, index) => {
      if (isTarget && index === to.beforeIndex) out.push(item)
      const ownPath = [...containerPath, index]
      const onTargetSpine =
        ownPath.length <= to.containerPath.length &&
        ownPath.every((v, at) => to.containerPath[at] === v)
      out.push(
        isGroup(child)
          ? { ...child, items: rebuild(child.items, ownPath) }
          : onTargetSpine && child.page
            ? { ...child, items: rebuild(child.items ?? [], ownPath) }
            : child,
      )
    })
    if (isTarget && to.beforeIndex === items.length) out.push(item)
    return out
  }

  const sections = nav.sections.map((section, i) => ({
    ...section,
    items: rebuild(section.items, [i]),
  }))
  return { ok: true, nav: { ...nav, sections } }
}

/**
 * Take an item out of the tree, children and all.
 *
 * What a delete needs, and what a rename that moves a page between sections
 * is built from. A group's children go with it — the alternative is deciding
 * on somebody's behalf where nine orphans should land.
 */
export function removeNavItem(nav: Nav, from: NavPath): MoveOutcome {
  if (from.length < 2) return { ok: false, reason: 'not-found' }
  if (!itemAt(nav, from)) return { ok: false, reason: 'not-found' }
  const fromContainer = from.slice(0, -1)
  const fromIndex = from[from.length - 1]!

  const rebuild = (items: NavItem[], containerPath: NavContainerPath): NavItem[] => {
    const isSource = sameContainerPath(containerPath, fromContainer)
    const out: NavItem[] = []
    items.forEach((child, index) => {
      if (isSource && index === fromIndex) return
      const ownPath = [...containerPath, index]
      out.push(isGroup(child) ? { ...child, items: rebuild(child.items, ownPath) } : child)
    })
    return out
  }

  const sections = nav.sections.map((section, i) => ({
    ...section,
    items: rebuild(section.items, [i]),
  }))
  return { ok: true, nav: { ...nav, sections } }
}

/**
 * Change the label an item carries — a group's name, a separator's caption,
 * or the override a page is listed under.
 *
 * An empty label REMOVES the override rather than writing an empty string:
 * a page listed as "" is a row with no name, and the page's own title is the
 * honest answer to what it should say instead.
 */
export function renameNavItem(nav: Nav, at: NavPath, label: string): MoveOutcome {
  const item = itemAt(nav, at)
  if (!item) return { ok: false, reason: 'not-found' }
  const trimmed = label.trim()
  if ((item.label ?? '') === trimmed) return { ok: false, reason: 'no-op' }

  const container = at.slice(0, -1)
  const index = at[at.length - 1]!
  const rebuild = (items: NavItem[], containerPath: NavContainerPath): NavItem[] =>
    items.map((child, i) => {
      const ownPath = [...containerPath, i]
      const renamed =
        sameContainerPath(containerPath, container) && i === index
          ? trimmed === ''
            ? (({ label: _gone, ...rest }) => rest as NavItem)(child)
            : { ...child, label: trimmed }
          : child
      return isGroup(renamed) ? { ...renamed, items: rebuild(renamed.items, ownPath) } : renamed
    })

  const sections = nav.sections.map((section, i) => ({
    ...section,
    items: rebuild(section.items, [i]),
  }))
  return { ok: true, nav: { ...nav, sections } }
}

/** One place a new page can be put, as a person would read it. */
export interface NavPlace {
  /** The container the page would be appended to. */
  target: DropTarget
  /** "Guides", or "Guides › Getting started" — the trail, not a path. */
  label: string
  /** How deep it sits, for indenting the picker. */
  depth: number
}

/**
 * Every place in the navigation something can be put, top to bottom.
 *
 * Sections first, then the groups and parent pages inside them, each named by
 * the trail a reader would follow. This is what the "Place under" picker
 * offers, and it is deliberately built from the tree rather than from a list
 * of sections: a page can be a parent here, and a picker that only offered
 * sections would make the cascade unreachable to anybody not dragging.
 */
export function navPlaces(nav: Nav, titleOf?: (pageId: string) => string | null): NavPlace[] {
  const out: NavPlace[] = []
  const walk = (
    items: NavItem[],
    containerPath: NavContainerPath,
    trail: string,
    depth: number,
  ) => {
    items.forEach((item, index) => {
      const ownPath = [...containerPath, index]
      if (isGroup(item)) {
        const label = item.label ?? 'Group'
        out.push({
          target: { containerPath: ownPath, beforeIndex: item.items.length },
          label: `${trail} › ${label}`,
          depth,
        })
        walk(item.items, ownPath, `${trail} › ${label}`, depth + 1)
        return
      }
      // A page is a container too — GitBook's cascade. Offered so somebody can
      // put a page UNDER another page without dragging it there.
      if (item.page) {
        const children = item.items ?? []
        const label = item.label ?? titleOf?.(item.page) ?? item.page
        out.push({
          target: { containerPath: ownPath, beforeIndex: children.length },
          label: `${trail} › ${label}`,
          depth,
        })
        walk(children, ownPath, `${trail} › ${label}`, depth + 1)
      }
    })
  }

  nav.sections.forEach((section, i) => {
    out.push({
      target: { containerPath: [i], beforeIndex: section.items.length },
      label: section.label,
      depth: 0,
    })
    walk(section.items, [i], section.label, 1)
  })
  return out
}

/**
 * Where a page sits in the tree, by its id — or null when nothing lists it.
 *
 * The one lookup that goes the other way. Every other function here is given
 * a path; a caller who has a page and needs its path (to put a copy beside
 * it, say) had to walk the tree itself, which is how two walks that disagree
 * get written.
 */
export function findNavPath(nav: Nav, pageId: string): NavPath | null {
  const walk = (items: NavItem[], containerPath: NavContainerPath): NavPath | null => {
    for (const [index, item] of items.entries()) {
      const ownPath = [...containerPath, index]
      if (item.page === pageId) return ownPath
      const found = item.items ? walk(item.items, ownPath) : null
      if (found) return found
    }
    return null
  }
  for (const [i, section] of nav.sections.entries()) {
    const found = walk(section.items, [i])
    if (found) return found
  }
  return null
}

/**
 * Add a section — the headings the sidebar groups pages under.
 *
 * A new workspace is seeded with `sections: []`, so until this existed the
 * only way to make the first one was to open nav.yaml and write YAML: the
 * product asked a writer to learn its storage format before they could list
 * their second page. `at` inserts among the existing sections; omitted, it
 * appends.
 */
export function addNavSection(nav: Nav, label: string, at?: number): MoveOutcome {
  const named = label.trim()
  if (named === '') return { ok: false, reason: 'no-op' }
  const index = at ?? nav.sections.length
  if (index < 0 || index > nav.sections.length) return { ok: false, reason: 'not-found' }
  const sections = [...nav.sections]
  sections.splice(index, 0, { label: named, items: [] })
  return { ok: true, nav: { ...nav, sections } }
}

/** Change a section's heading. An empty name is refused: a heading is its name. */
export function renameNavSection(nav: Nav, index: number, label: string): MoveOutcome {
  const section = nav.sections[index]
  if (!section) return { ok: false, reason: 'not-found' }
  const named = label.trim()
  if (named === '' || named === section.label) return { ok: false, reason: 'no-op' }
  const sections = nav.sections.map((s, i) => (i === index ? { ...s, label: named } : s))
  return { ok: true, nav: { ...nav, sections } }
}

/**
 * Remove a section.
 *
 * Refused while it still holds anything: the pages inside would become
 * orphans, and the commit would then be refused for a reason the writer
 * cannot see from the tree. Emptying it first is a decision about each page,
 * which is where that decision belongs.
 */
export function removeNavSection(nav: Nav, index: number): MoveOutcome {
  const section = nav.sections[index]
  if (!section) return { ok: false, reason: 'not-found' }
  if (section.items.length > 0) return { ok: false, reason: 'not-empty' }
  return { ok: true, nav: { ...nav, sections: nav.sections.filter((_, i) => i !== index) } }
}
